"use client";

/**
 * Password recovery, both halves.
 *
 * Which half is shown is decided by the presence of `?token=`, not by a tab:
 * somebody arriving from the mail wants the "set a new password" form and
 * nothing else, and somebody who clicked "I forgot" wants the address field.
 *
 * The copy here is under a hard constraint. The server answers /forgot
 * IDENTICALLY whether or not the address belongs to an account, so this screen
 * may never say "we have sent you an email" — it does not know that, and
 * saying it would also turn the screen into the membership oracle the server
 * refuses to be. Every string says "if an account exists".
 */

import { useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Loader2, MailCheck, KeyRound, AlertTriangle, CheckCircle2 } from "lucide-react";
import { requestPasswordReset, confirmPasswordReset } from "@/lib/account/password-reset-api";

const MIN_PASSWORD = 8;
const MAX_PASSWORD = 72; // bcrypt's own limit, enforced server-side too.

export default function ResetPasswordForm({ locale, token }: { locale: string; token?: string }) {
    return token ? <SetNewPassword locale={locale} token={token} /> : <RequestLink locale={locale} />;
}

function RequestLink({ locale }: { locale: string }) {
    const t = useTranslations("passwordReset");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [state, setState] = useState<"idle" | "sent" | "throttled" | "unavailable" | "error">("idle");

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy || !email.trim()) return;
        setBusy(true);
        const out = await requestPasswordReset(email.trim(), locale);
        setBusy(false);
        setState(
            out.kind === "requested" ? "sent"
                : out.kind === "throttled" ? "throttled"
                    : out.kind === "email_unavailable" ? "unavailable"
                        : "error",
        );
    };

    if (state === "sent") {
        return (
            <Panel icon={<MailCheck className="h-6 w-6 text-emerald-700" />} title={t("sentTitle")}>
                {/* NOT "we sent you a mail": the server does not tell us whether
                    the address belongs to an account, and this screen must not
                    claim more than it knows. */}
                <p className="text-sm text-slate-600">{t("sentBody")}</p>
                <p className="mt-2 text-sm text-slate-500">{t("sentSpam")}</p>
                <Link href={`/${locale}/giris`} className="mt-6 inline-flex min-h-[44px] items-center rounded-xl bg-slate-900 px-5 text-sm font-bold text-white transition hover:bg-slate-800">
                    {t("backToLogin")}
                </Link>
            </Panel>
        );
    }

    if (state === "unavailable") {
        return (
            <Panel icon={<AlertTriangle className="h-6 w-6 text-amber-600" />} title={t("unavailableTitle")}>
                {/* The honest answer when no mail can leave the building. Telling
                    somebody who has LOST access to "check your inbox" sends them
                    to wait for a message nothing could send. */}
                <p className="text-sm text-slate-600">{t("unavailableBody")}</p>
                <Link href={`/${locale}/iletisim`} className="mt-6 inline-flex min-h-[44px] items-center rounded-xl bg-slate-900 px-5 text-sm font-bold text-white transition hover:bg-slate-800">
                    {t("contactSupport")}
                </Link>
            </Panel>
        );
    }

    return (
        <Panel icon={<KeyRound className="h-6 w-6 text-slate-700" />} title={t("requestTitle")}>
            <p className="text-sm text-slate-600">{t("requestSub")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                    <label htmlFor="reset-email" className="mb-1.5 block text-sm font-semibold text-slate-700">{t("email")}</label>
                    <input
                        id="reset-email" type="email" required autoComplete="email" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="min-h-[44px] w-full rounded-xl border border-slate-200 px-4 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                </div>
                {state === "throttled" && <Notice tone="amber">{t("throttled")}</Notice>}
                {state === "error" && <Notice tone="rose">{t("genericError")}</Notice>}
                <button type="submit" disabled={busy} className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-50">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                    {busy ? t("processing") : t("requestCta")}
                </button>
            </form>
            <Link href={`/${locale}/giris`} className="mt-4 inline-block text-sm font-semibold text-slate-500 transition hover:text-emerald-800">
                {t("backToLogin")}
            </Link>
        </Panel>
    );
}

function SetNewPassword({ locale, token }: { locale: string; token: string }) {
    const t = useTranslations("passwordReset");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        // Checked here only to save a round trip; the server enforces the same
        // bounds and is the one that decides.
        if (password.length < MIN_PASSWORD) { setError(t("tooShort", { min: MIN_PASSWORD })); return; }
        if (password.length > MAX_PASSWORD) { setError(t("tooLong", { max: MAX_PASSWORD })); return; }
        if (password !== confirm) { setError(t("mismatch")); return; }
        setError(null);
        setBusy(true);
        const out = await confirmPasswordReset(token, password);
        setBusy(false);
        if (out.kind === "done") { setDone(true); return; }
        setError(
            out.kind === "token_rejected" ? t("tokenRejected")
                : out.kind === "password_rejected" ? (out.message || t("genericError"))
                    : t("genericError"),
        );
    };

    if (done) {
        return (
            <Panel icon={<CheckCircle2 className="h-6 w-6 text-emerald-700" />} title={t("doneTitle")}>
                {/* Every session really was invalidated server-side
                    (session_version is bumped), so this is a statement of fact,
                    not reassurance. */}
                <p className="text-sm text-slate-600">{t("doneBody")}</p>
                <Link href={`/${locale}/giris`} className="mt-6 inline-flex min-h-[44px] items-center rounded-xl bg-emerald-600 px-5 text-sm font-bold text-white transition hover:bg-emerald-700">
                    {t("goLogin")}
                </Link>
            </Panel>
        );
    }

    return (
        <Panel icon={<KeyRound className="h-6 w-6 text-slate-700" />} title={t("setTitle")}>
            <p className="text-sm text-slate-600">{t("setSub")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                    <label htmlFor="new-password" className="mb-1.5 block text-sm font-semibold text-slate-700">{t("newPassword")}</label>
                    <input
                        id="new-password" type="password" required autoComplete="new-password"
                        minLength={MIN_PASSWORD} maxLength={MAX_PASSWORD}
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        className="min-h-[44px] w-full rounded-xl border border-slate-200 px-4 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                    <p className="mt-1 text-xs text-slate-500">{t("rule", { min: MIN_PASSWORD })}</p>
                </div>
                <div>
                    <label htmlFor="confirm-password" className="mb-1.5 block text-sm font-semibold text-slate-700">{t("confirmPassword")}</label>
                    <input
                        id="confirm-password" type="password" required autoComplete="new-password"
                        value={confirm} onChange={(e) => setConfirm(e.target.value)}
                        className="min-h-[44px] w-full rounded-xl border border-slate-200 px-4 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                </div>
                {error && <Notice tone="rose">{error}</Notice>}
                <button type="submit" disabled={busy} className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-50">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                    {busy ? t("processing") : t("setCta")}
                </button>
            </form>
        </Panel>
    );
}

function Panel({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
    return (
        <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <div className="mb-4 flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100">{icon}</span>
                <h1 className="text-xl font-extrabold text-slate-900">{title}</h1>
            </div>
            {children}
        </div>
    );
}

function Notice({ tone, children }: { tone: "amber" | "rose"; children: React.ReactNode }) {
    const cls = tone === "amber"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-rose-200 bg-rose-50 text-rose-900";
    return (
        <p role="alert" className={`rounded-xl border px-4 py-3 text-sm font-medium ${cls}`}>
            {children}
        </p>
    );
}
